'use client';

import { useState } from 'react';
import { twMerge } from 'tailwind-merge';
import { useAnalytics } from '@domain/analytics/AnalyticsContext';
import { trackEvent } from '@domain/analytics/trackEvent';
import {
  PlaceTopWaterWithCatchesNodeFields,
  RegionTopWaterNodeFields,
} from '../../generated/graphql';
import { AppDownloadModal } from '../AppDownloadModal/AppDownloadModal';
import { Button } from '../Clickables/Buttons/Button';
import { Heading } from '../Heading/Heading';
import { FishingWaterCard } from './components/FishingWaterCard';
import {
  FISHINGWATERSGRID_MAX_CATCHES_DESKTOP,
  FISHINGWATERSGRID_MAX_CATCHES_MOBILE,
  FISHINGWATERSGRID_MAX_CATCHES_TABLET,
} from './constants/constants';

import { useIsTouchDevice } from '@hooks/useIsTouchDevice';
import { useIsMobileScreenWidthOrThinner } from '@hooks/useScreenWidth';
import { Pill } from '../Clickables/Pill/Pill';
import appdownloadPhone from './assets/AppdownloadPhone.png';
import appdownloadPhoneMobile from './assets/AppdownloadPhoneMobile.png';
import fakeFiltersPhone from './assets/FakeFiltersPhone.png';
import fakeFiltersPhoneMobile from './assets/FakeFiltersPhoneMobile.png';
interface IProps {
  className?: string;
  id: string;
  fakeFilters?: string[];
  fishingWaters: PlaceTopWaterWithCatchesNodeFields[] | RegionTopWaterNodeFields[];
  title: string;
  sourceId?: string;
}
export const FishingWatersGrid = ({
  className,
  fishingWaters,
  fakeFilters,
  id,
  title,
  sourceId,
}: IProps) => {
  const [isAppDownloadModalOpen, setIsAppDownloadModalOpen] = useState(false);
  const [isFakeFiltersDownloadModalOpen, setIsFakeFiltersDownloadModalOpen] = useState(false);
  const [activeFakeFilter, setActiveFakeFilter] = useState<number | undefined>();
  const isMobileScreenWidthOrThinner = useIsMobileScreenWidthOrThinner();
  const isTouchDevice = useIsTouchDevice();

  const { currentPage } = useAnalytics();
  const hasFakeFilters = fakeFilters && fakeFilters.length > 0;

  const handleShowMoreClick = () => {
    trackEvent({
      name: 'mykiss_cta_button_clicked',
      properties: {
        source: 'FishingWatersGrid',
        source_id: sourceId,
        type: 'downloadModal',
        page: currentPage,
      },
    });
    setIsAppDownloadModalOpen(true);
  };

  const handleFakeFilterClick = (index: number) => {
    trackEvent({
      name: 'mykiss_cta_button_clicked',
      properties: {
        source: 'FishingWatersGridFakeFilters',
        source_id: sourceId,
        type: 'downloadModal',
        page: currentPage,
      },
    });
    setActiveFakeFilter(index);
    setIsFakeFiltersDownloadModalOpen(true);
  };

  return (
    <div
      id={id}
      className={twMerge(
        'w-full scroll-mt-(--navigation-scrolltop-offset-mobile) px-6 sm:mb-16 sm:scroll-mt-(--navigation-scrolltop-offset) sm:px-0',
        className,
      )}
    >
      <Heading className={hasFakeFilters ? 'mb-4' : 'mb-6 sm:mb-10'} variant="sm" as="h2">
        {title}
      </Heading>
      {hasFakeFilters && (
        <div
          className={`-mx-6 mb-4 flex flex-row flex-nowrap gap-2 overflow-auto whitespace-nowrap sm:mx-0 ${isTouchDevice ? 'scrollbar-none' : ''}`}
        >
          {fakeFilters.map((filter, index) => (
            <Pill
              className={`${index === 0 ? 'ml-6 sm:ml-0' : ''} ${index === fakeFilters.length - 1 ? 'mr-6 sm:mr-0' : ''}`}
              variant="whiteBg"
              size="sm"
              isActive={activeFakeFilter === index}
              key={filter}
              onClick={() => {
                handleFakeFilterClick(index);
              }}
            >
              {filter}
            </Pill>
          ))}
        </div>
      )}
      <div className="grid grid-cols-4 gap-2 sm:grid-cols-12 sm:gap-4">
        {fishingWaters.map((fishingWater, index) => (
          <FishingWaterCard
            className={`test-${index} col-span-full sm:col-span-6 md:col-span-4 ${index >= FISHINGWATERSGRID_MAX_CATCHES_MOBILE ? 'hidden' : ''} ${index >= FISHINGWATERSGRID_MAX_CATCHES_TABLET ? 'sm:hidden' : ''} ${index >= FISHINGWATERSGRID_MAX_CATCHES_DESKTOP ? 'md:hidden' : ''}`}
            fishingWater={fishingWater}
            key={fishingWater._id}
          />
        ))}
      </div>
      <Button
        size="sm"
        className="mt-4 w-full p-2"
        variant="textPrimary"
        onClick={handleShowMoreClick}
      >
        Show more fishing spots
      </Button>
      <AppDownloadModal
        appDownloadSource="FishingWatersGrid"
        appDownloadSourceId={sourceId}
        modalHeader={'Find prime fishing spots anywhere'}
        modalHeaderVariant="sm"
        modalText="Track catches, find big fish hot spots and check the weekly quality rating of waters."
        isModalOpen={isAppDownloadModalOpen}
        image={appdownloadPhone}
        imageMobile={appdownloadPhoneMobile}
        imageWrapperClassName="bg-tuna-tint px-8 sm:p-6 md:mr-4"
        onCloseModal={() => setIsAppDownloadModalOpen(false)}
        textWrapperClassName="mt-6"
        showQRCodeHint
        showTextOnMobile
      />
      {hasFakeFilters && (
        <AppDownloadModal
          appDownloadSource="FishingReportsGrid"
          appDownloadSourceId={sourceId}
          closeButtonVariant={isMobileScreenWidthOrThinner ? 'textNone' : 'controlWhiteBg'}
          modalHeader={'Find more fish, faster – Use catch filters in the Fishbrain app'}
          modalHeaderClassName="sm:mr-16 md:mr-16"
          modalHeaderVariant="sm"
          modalText=""
          isModalOpen={isFakeFiltersDownloadModalOpen}
          image={fakeFiltersPhone}
          imageMobile={fakeFiltersPhoneMobile}
          imageWrapperClassName="bg-tuna-tint px-8 sm:p-6 md:mr-4"
          onCloseModal={() => {
            setIsFakeFiltersDownloadModalOpen(false);
            setActiveFakeFilter(undefined);
          }}
          textWrapperClassName="mt-6 sm:pb-0 md:pr-4 md:pb-4"
          showQRCodeHint
          width="700px"
        />
      )}
    </div>
  );
};
